import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { createAxiosInstance } from '../../utils/Axios';
import './leadcreatemodal.css';

// Constants for follow-up actions and categories
const FOLLOW_UP_ACTION = {
  "Call Customer": 0,
  "Not answering calls": 10,
  "Asking callback repeatedly": 20,
  "Shared requirements": 40,
  "Shared floor plan": 50,
  "Discussed budget openly": 60,
  "Asked for pre-estimate": 65,
  "Requested site visit": 75,
  "Site visit completed": 80,
  "Asked for revised estimate": 85,
  "Negotiation ongoing": 90,
  "Discussing start date": 95,
  "Ready to start / Advance": 100,
};

const PROBABILITY_TO_ACTION = {
  0: "Call Customer",
  10: "Not answering calls",
  20: "Asking callback repeatedly",
  40: "Shared requirements",
  50: "Shared floor plan",
  60: "Discussed budget openly",
  65: "Asked for pre-estimate",
  75: "Requested site visit",
  80: "Site visit completed",
  85: "Asked for revised estimate",
  90: "Negotiation ongoing",
  95: "Discussing start date",
  100: "Ready to start / Advance"
};

function getLeadCategory(score) {
  if (score <= 20) return "Cold Lead";
  if (score <= 40) return "Warm Lead";
  if (score <= 60) return "Interested Lead";
  if (score <= 80) return "Hot Lead";
  return "High conversion";
}

function getStageFromCategory(category) {
  const stageMap = {
    "Cold Lead": "New Lead",
    "Warm Lead": "Qualified",
    "Interested Lead": "Proposal",
    "Hot Lead": "Negotiation",
    "High conversion": "Won"
  };
  return stageMap[category] || "New Lead";
}

const DEFAULT_STAGE_STATUS = {
  "New Lead": "Contact Pending",
  "Qualified": "Requirement Discussion",
  "Proposal": "Assigned to Sales",
  "Negotiation": "Waiting for Decision",
  "Won": "Won",
  "Lost": "Lost - No Response",
  "Closed": "Won"
};

const AVAILABLE_STAGES = [
  "New Lead",
  "Qualified",
  "Proposal",
  "Negotiation",
  "Won",
  "Lost",
  "Closed"
];

const ALL_STAGE_STATUSES = [
  "Contact Pending",
  "No Response",
  "Call Back Later",
  "Qualification in Progress",
  "Requirement Discussion",
  "Waiting for Floor Plan",
  "Site Visit Scheduled",
  "Site Visit Completed",
  "Information Collection",
  "Proposal Handover",
  "Assigned to Sales",
  "Proposal in Progress",
  "Proposal Ready",
  "Proposal Shared",
  "Waiting for Decision",
  "Price Discussion",
  "Scope Revision",
  "Won",
  "Lost - No Response",
  "Lost - Budget",
  "Lost - Competitor",
  "Lost - Postponed",
  "Lost - Scope Of Work"
];

const AddLeadModal = ({
  isOpen,
  onClose,
  onSuccess,
  tenantId,
  campaignsList = [],
  customFields = [],
  allUsers = [],
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Personal Info
    name: '',
    email: '',
    phone: '',
    source: '',
    sourceId: '',
    address: '',
    organization: '',
    
    // Lead Details
    campaignId: '',
    launch_30_days: '',
    leadStatus: 'New',
    customLeadStatus: '',
    paymentStatus: 'Pending',
    amount: '',
    probability: 0,
    expectedStartDate: '',
    
    // Auto-updated fields
    followUpAction: 'Call Customer',
    leadCategory: 'Cold Lead',
    stage: 'New Lead',
    stageStatus: 'Contact Pending',
    
    // Management
    assignedTo: '',
    nextAction: '',
    nextActionDueDate: '',
    notes: '',
    
    // Custom fields
    customFieldValues: {},
  });

  // Track expanded sections
  const [expandedSections, setExpandedSections] = useState({
    personalInfo: true,
    leadDetails: true,
    autoUpdatedFields: true,
    leadManagement: true,
    customFields: true
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Initialize custom field values from customFields
      const initialCustomValues = {};
      customFields.forEach((field) => {
        initialCustomValues[field.fieldKey] = field.defaultValue || '';
      });
      
      // Set default values for auto fields
      const defaultFollowUpAction = 'Call Customer';
      const defaultScore = FOLLOW_UP_ACTION[defaultFollowUpAction] || 0;
      const defaultCategory = getLeadCategory(defaultScore);
      const defaultStage = getStageFromCategory(defaultCategory);
      const defaultStageStatus = DEFAULT_STAGE_STATUS[defaultStage] || 'Contact Pending';
      
      setFormData(prev => ({
        ...prev,
        customFieldValues: initialCustomValues,
        followUpAction: defaultFollowUpAction,
        leadCategory: defaultCategory,
        stage: defaultStage,
        stageStatus: defaultStageStatus,
      }));
      
      // Reset to basic tab
      setActiveTab('basic');
    }
  }, [isOpen, customFields]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle custom field changes
  const handleCustomFieldChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      customFieldValues: {
        ...prev.customFieldValues,
        [fieldKey]: value,
      },
    }));
  };

  // Handle follow-up action change (auto-updates probability, category, stage)
  const handleFollowUpActionChange = (action) => {
    const score = FOLLOW_UP_ACTION[action] || 0;
    const category = getLeadCategory(score);
    const stage = getStageFromCategory(category);
    const stageStatus = DEFAULT_STAGE_STATUS[stage] || 'Contact Pending';
    
    setFormData(prev => ({
      ...prev,
      followUpAction: action,
      probability: score,
      leadCategory: category,
      stage: stage,
      stageStatus: stageStatus,
    }));
  };

  // Handle probability change (auto-updates follow-up action, category, stage)
  const handleProbabilityChange = (value) => {
    const numValue = Number(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      toast.error('Probability must be between 0 and 100');
      return;
    }
    
    // Find the closest matching action based on probability
    const probabilities = Object.keys(PROBABILITY_TO_ACTION).map(Number);
    const closestProbability = probabilities.reduce((prev, curr) => {
      return Math.abs(curr - numValue) < Math.abs(prev - numValue) ? curr : prev;
    });
    
    const action = PROBABILITY_TO_ACTION[closestProbability];
    const category = getLeadCategory(closestProbability);
    const stage = getStageFromCategory(category);
    const stageStatus = DEFAULT_STAGE_STATUS[stage] || 'Contact Pending';
    
    setFormData(prev => ({
      ...prev,
      probability: numValue,
      followUpAction: action,
      leadCategory: category,
      stage: stage,
      stageStatus: stageStatus,
    }));
  };

  // Handle stage change (auto-updates stage status)
  const handleStageChange = (newStage) => {
    const stageStatus = DEFAULT_STAGE_STATUS[newStage] || 'Contact Pending';
    setFormData(prev => ({
      ...prev,
      stage: newStage,
      stageStatus: stageStatus,
    }));
  };

  // Handle stage status change (auto-updates stage)
  const handleStageStatusChange = (status) => {
    // Find corresponding stage from status
    const statusToStageMap = {
      "Contact Pending": "New Lead",
      "No Response": "New Lead",
      "Call Back Later": "New Lead",
      "Qualification in Progress": "New Lead",
      "Requirement Discussion": "Qualified",
      "Waiting for Floor Plan": "Qualified",
      "Site Visit Scheduled": "Qualified",
      "Site Visit Completed": "Qualified",
      "Information Collection": "Qualified",
      "Proposal Handover": "Qualified",
      "Assigned to Sales": "Proposal",
      "Proposal in Progress": "Proposal",
      "Proposal Ready": "Proposal",
      "Proposal Shared": "Proposal",
      "Waiting for Decision": "Negotiation",
      "Price Discussion": "Negotiation",
      "Scope Revision": "Negotiation",
      "Won": "Won",
      "Lost - No Response": "Lost",
      "Lost - Budget": "Lost",
      "Lost - Competitor": "Lost",
      "Lost - Postponed": "Lost",
      "Lost - Scope Of Work": "Lost"
    };
    
    const newStage = statusToStageMap[status] || formData.stage;
    setFormData(prev => ({
      ...prev,
      stageStatus: status,
      stage: newStage,
    }));
  };

  // Handle lead category change
  const handleLeadCategoryChange = (category) => {
    setFormData(prev => ({ ...prev, leadCategory: category }));
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Validate form before submit
  const validateForm = () => {
    if (!formData.name || !formData.name.trim()) {
      toast.error('Please enter the lead name');
      return false;
    }
    if (!formData.email || !formData.email.trim()) {
      toast.error('Please enter the email address');
      return false;
    }
    if (!formData.phone || !formData.phone.trim()) {
      toast.error('Please enter the phone number');
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';

      // Build form data payload
      const formDataPayload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address || '',
        organization: formData.organization || '',
        launch_30_days: formData.launch_30_days || '',
        ...formData.customFieldValues,
      };

      // Build complete payload
      const payload = {
        campaignId: formData.campaignId || undefined,
        source: formData.source || '',
        sourceId: formData.sourceId || '',
        formData: formDataPayload,
        leadStatus: formData.leadStatus === 'Others' ? formData.customLeadStatus : formData.leadStatus,
        paymentStatus: formData.paymentStatus || 'Pending',
        amount: formData.amount ? parseFloat(formData.amount) : undefined,
        probability: formData.probability || 0,
        assignedTo: formData.assignedTo || undefined,
        followUpAction: formData.followUpAction || 'Call Customer',
        leadCategory: formData.leadCategory || 'Cold Lead',
        stage: formData.stage || 'New Lead',
        stageStatus: formData.stageStatus || 'Contact Pending',
        expectedStartDate: formData.expectedStartDate || undefined,
        nextAction: formData.nextAction || undefined,
        nextActionDueDate: formData.nextActionDueDate || undefined,
        notes: formData.notes ? [formData.notes] : undefined,
        customFields: formData.customFieldValues,
        tenantId: tenantId,
      };

      const response = await axiosInstance.post(`${apiUrl}/leads`, payload);

      if (response.data && response.data.success) {
        toast.success('Lead created successfully!');
        
        // Call onSuccess with the created lead data
        if (onSuccess) {
          onSuccess(response.data.lead || response.data.data);
        }
        
        onClose();
        resetForm();
      } else {
        toast.error(response.data?.message || 'Failed to create lead');
      }
    } catch (error) {
      console.error('Failed to create lead:', error);
      toast.error(error?.response?.data?.message || 'Failed to create lead. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      source: '',
      sourceId: '',
      address: '',
      organization: '',
      campaignId: '',
      launch_30_days: '',
      leadStatus: 'New',
      customLeadStatus: '',
      paymentStatus: 'Pending',
      amount: '',
      probability: 0,
      expectedStartDate: '',
      followUpAction: 'Call Customer',
      leadCategory: 'Cold Lead',
      stage: 'New Lead',
      stageStatus: 'Contact Pending',
      assignedTo: '',
      nextAction: '',
      nextActionDueDate: '',
      notes: '',
      customFieldValues: {},
    });
  };

  // Get custom field value or default
  const getCustomFieldValue = (fieldKey) => {
    return formData.customFieldValues[fieldKey] || '';
  };

  // Get custom field type
  const getCustomFieldType = (fieldKey) => {
    const field = customFields.find(f => f.fieldKey === fieldKey);
    return field?.fieldType || 'text';
  };

  // Get custom field options (for dropdown, radio, checkbox)
  const getCustomFieldOptions = (fieldKey) => {
    const field = customFields.find(f => f.fieldKey === fieldKey);
    return field?.options || [];
  };

  // Get custom field label
  const getCustomFieldLabel = (fieldKey) => {
    const field = customFields.find(f => f.fieldKey === fieldKey);
    return field?.fieldName || fieldKey;
  };

  // Get custom field required status
  const isCustomFieldRequired = (fieldKey) => {
    const field = customFields.find(f => f.fieldKey === fieldKey);
    return field?.isRequired || false;
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          width: '1180px',
          maxWidth: '96vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #F3F4F6',
            flexShrink: 0,
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
              }}
            >
              Add New Lead
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6B7280' }}>
              Create a new lead with all details
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6B7280',
              fontSize: '24px',
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #F3F4F6',
            padding: '0 24px',
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setActiveTab('basic')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'basic' ? '600' : '500',
              color: activeTab === 'basic' ? '#7C3AED' : '#6B7280',
              borderBottom: activeTab === 'basic' ? '2px solid #7C3AED' : '2px solid transparent',
              transition: 'all 0.2s',
            }}
          >
            Basic Info
          </button>
          <button
            onClick={() => setActiveTab('details')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'details' ? '600' : '500',
              color: activeTab === 'details' ? '#7C3AED' : '#6B7280',
              borderBottom: activeTab === 'details' ? '2px solid #7C3AED' : '2px solid transparent',
              transition: 'all 0.2s',
            }}
          >
            Lead Details
          </button>
          <button
            onClick={() => setActiveTab('advanced')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'advanced' ? '600' : '500',
              color: activeTab === 'advanced' ? '#7C3AED' : '#6B7280',
              borderBottom: activeTab === 'advanced' ? '2px solid #7C3AED' : '2px solid transparent',
              transition: 'all 0.2s',
            }}
          >
            Advanced
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div>
              {/* Personal Information Section */}
              <div
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: expandedSections.personalInfo ? '12px' : '0',
                  }}
                  onClick={() => toggleSection('personalInfo')}
                >
                  <h4
                    style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                    }}
                  >
                    Personal Information
                  </h4>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#000000',
                      fontSize: '20px',
                      padding: '4px 8px',
                    }}
                  >
                    {expandedSections.personalInfo ? '−' : '+'}
                  </button>
                </div>

                {expandedSections.personalInfo && (
                  <div>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Name <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Enter full name"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                          onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Email <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter email address"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                          onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Phone No <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <div style={{ display: 'flex' }}>
                          <select
                            style={{
                              padding: '8px 12px',
                              backgroundColor: '#fff',
                              border: '1px solid #E5E7EB',
                              borderRadius: '6px 0 0 6px',
                              outline: 'none',
                              fontSize: '14px',
                              color: '#374151',
                              cursor: 'pointer',
                              borderRight: 'none',
                            }}
                          >
                            <option>+91</option>
                          </select>
                          <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="Enter phone number"
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              backgroundColor: '#fff',
                              border: '1px solid #E5E7EB',
                              borderRadius: '0 6px 6px 0',
                              outline: 'none',
                              fontSize: '14px',
                              color: '#111827',
                              transition: 'border-color 0.2s',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                            onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Platform/Source
                        </label>
                        <select
                          name="source"
                          value={formData.source}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer',
                            appearance: 'none',
                            boxSizing: 'border-box',
                            backgroundImage:
                              'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 14px center',
                            backgroundSize: '16px',
                          }}
                        >
                          <option value="">Select Source</option>
                          <option value="e-mail">E-mail</option>
                          <option value="Social Media">Social Media</option>
                          <option value="Website">Website</option>
                          <option value="Import">Import</option>
                          <option value="Referral">Referral</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          When do you want to start your interior
                        </label>
                        <input
                          type="text"
                          name="sourceId"
                          value={formData.sourceId}
                          onChange={handleInputChange}
                          placeholder="When do you want to start your interior"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                          onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          City/Address
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Enter city or address"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                          onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Organization
                        </label>
                        <input
                          type="text"
                          name="organization"
                          value={formData.organization}
                          onChange={handleInputChange}
                          placeholder="Enter organization name"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                          onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lead Details Tab */}
          {activeTab === 'details' && (
            <div>
              {/* Lead Details Section */}
              <div
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: expandedSections.leadDetails ? '12px' : '0',
                  }}
                  onClick={() => toggleSection('leadDetails')}
                >
                  <h4
                    style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                    }}
                  >
                    Lead Details
                  </h4>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#000000',
                      fontSize: '20px',
                      padding: '4px 8px',
                    }}
                  >
                    {expandedSections.leadDetails ? '−' : '+'}
                  </button>
                </div>

                {expandedSections.leadDetails && (
                  <div>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Campaign
                        </label>
                        <select
                          name="campaignId"
                          value={formData.campaignId}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer',
                            appearance: 'none',
                            boxSizing: 'border-box',
                            backgroundImage:
                              'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 14px center',
                            backgroundSize: '16px',
                          }}
                        >
                          <option value="">Select Campaign</option>
                          {campaignsList.map((camp) => {
                            // Safely extract display name - ensure it's a string
                            const displayName = camp?.title || camp?.name || camp?.campaignName || 'Unnamed Campaign';
                            return (
                              <option key={camp._id || camp.id || Math.random()} value={camp._id || camp.id || ''}>
                                {String(displayName)}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Looking to launch within 30 days?
                        </label>
                        <select
                          name="launch_30_days"
                          value={formData.launch_30_days}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer',
                            appearance: 'none',
                            boxSizing: 'border-box',
                            backgroundImage:
                              'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 14px center',
                            backgroundSize: '16px',
                          }}
                        >
                          <option value="">Select</option>
                          <option value="Yes — urgent">Yes — urgent</option>
                          <option value="Possibly — need scope">
                            Possibly — need scope
                          </option>
                          <option value="Later — exploring">
                            Later — exploring
                          </option>
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Lead Status
                        </label>
                        <select
                          name="leadStatus"
                          value={formData.leadStatus}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer',
                            appearance: 'none',
                            boxSizing: 'border-box',
                            backgroundImage:
                              'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 14px center',
                            backgroundSize: '16px',
                          }}
                        >
                          <option value="New">New</option>
                          <option value="Contracted">Contracted</option>
                          <option value="Interested">Interested</option>
                          <option value="Qualified">Qualified</option>
                          <option value="Lost">Lost</option>
                          <option value="Converted">Converted</option>
                          <option value="Others">Others</option>
                        </select>
                        {formData.leadStatus === 'Others' && (
                          <input
                            type="text"
                            name="customLeadStatus"
                            value={formData.customLeadStatus}
                            onChange={handleInputChange}
                            placeholder="Enter custom status"
                            style={{
                              width: '100%',
                              marginTop: '8px',
                              padding: '8px 12px',
                              backgroundColor: '#fff',
                              border: '1px solid #E5E7EB',
                              borderRadius: '6px',
                              outline: 'none',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                            }}
                          />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Payment Status
                        </label>
                        <select
                          name="paymentStatus"
                          value={formData.paymentStatus}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer',
                            appearance: 'none',
                            boxSizing: 'border-box',
                            backgroundImage:
                              'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 14px center',
                            backgroundSize: '16px',
                          }}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Successful">Successful</option>
                          <option value="Failed">Failed</option>
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Budget (₹)
                        </label>
                        <input
                          type="number"
                          name="amount"
                          value={formData.amount}
                          onChange={handleInputChange}
                          placeholder="Enter budget"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                          onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#111827',
                          marginBottom: '4px',
                        }}
                      >
                        Expected Start Date
                      </label>
                      <input
                        type="date"
                        name="expectedStartDate"
                        value={formData.expectedStartDate}
                        onChange={handleInputChange}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#fff',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          outline: 'none',
                          fontSize: '14px',
                          color: '#111827',
                          boxSizing: 'border-box',
                          transition: 'border-color 0.2s',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                        onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div>
              {/* Auto Updated Fields Section */}
              <div
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: expandedSections.autoUpdatedFields ? '12px' : '0',
                  }}
                  onClick={() => toggleSection('autoUpdatedFields')}
                >
                  <h4
                    style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                    }}
                  >
                    Auto Updated Fields
                  </h4>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#000000',
                      fontSize: '20px',
                      padding: '4px 8px',
                    }}
                  >
                    {expandedSections.autoUpdatedFields ? '−' : '+'}
                  </button>
                </div>

                {expandedSections.autoUpdatedFields && (
                  <div>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Lead Category
                        </label>
                        <select
                          value={formData.leadCategory}
                          onChange={(e) => handleLeadCategoryChange(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer',
                            appearance: 'none',
                            boxSizing: 'border-box',
                            backgroundImage:
                              'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 14px center',
                            backgroundSize: '16px',
                          }}
                        >
                          <option value="Cold Lead">Cold Lead</option>
                          <option value="Warm Lead">Warm Lead</option>
                          <option value="Interested Lead">Interested Lead</option>
                          <option value="Hot Lead">Hot Lead</option>
                          <option value="High conversion">High conversion</option>
                        </select>
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#6B7280',
                            marginTop: '4px',
                          }}
                        >
                          Auto-updated based on probability
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Stage
                        </label>
                        <select
                          value={formData.stage}
                          onChange={(e) => handleStageChange(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer',
                            appearance: 'none',
                            boxSizing: 'border-box',
                            backgroundImage:
                              'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 14px center',
                            backgroundSize: '16px',
                          }}
                        >
                          {AVAILABLE_STAGES.map((stageOption) => (
                            <option key={stageOption} value={stageOption}>
                              {stageOption}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Stage Status
                        </label>
                        <select
                          value={formData.stageStatus}
                          onChange={(e) => handleStageStatusChange(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer',
                            appearance: 'none',
                            boxSizing: 'border-box',
                            backgroundImage:
                              'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 14px center',
                            backgroundSize: '16px',
                          }}
                        >
                          {ALL_STAGE_STATUSES.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Probability (%)
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.probability}
                            onChange={(e) => handleProbabilityChange(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              backgroundColor: '#fff',
                              border: '1px solid #E5E7EB',
                              borderRadius: '6px',
                              outline: 'none',
                              fontSize: '14px',
                              color: '#111827',
                              boxSizing: 'border-box',
                              transition: 'border-color 0.2s',
                            }}
                            onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                            onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#6B7280',
                            marginTop: '4px',
                          }}
                        >
                          Auto-updates Lead Category & Stage
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Task Completed
                        </label>
                        <select
                          value={formData.followUpAction}
                          onChange={(e) => handleFollowUpActionChange(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer',
                            appearance: 'none',
                            boxSizing: 'border-box',
                            backgroundImage:
                              'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 14px center',
                            backgroundSize: '16px',
                          }}
                        >
                          {Object.keys(FOLLOW_UP_ACTION).map((action) => (
                            <option key={action} value={action}>
                              {action} ({FOLLOW_UP_ACTION[action]}%)
                            </option>
                          ))}
                        </select>
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#6B7280',
                            marginTop: '4px',
                          }}
                        >
                          Auto-updates probability & stage
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Lead Management Section */}
              <div
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: expandedSections.leadManagement ? '12px' : '0',
                  }}
                  onClick={() => toggleSection('leadManagement')}
                >
                  <h4
                    style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                    }}
                  >
                    Lead Management
                  </h4>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#000000',
                      fontSize: '20px',
                      padding: '4px 8px',
                    }}
                  >
                    {expandedSections.leadManagement ? '−' : '+'}
                  </button>
                </div>

                {expandedSections.leadManagement && (
                  <div>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Assigned To
                        </label>
                        <select
                          name="assignedTo"
                          value={formData.assignedTo}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            cursor: 'pointer',
                            appearance: 'none',
                            boxSizing: 'border-box',
                            backgroundImage:
                              'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 14px center',
                            backgroundSize: '16px',
                          }}
                        >
                          <option value="">Unassigned</option>
                          {allUsers.map((user) => (
                            <option key={user._id} value={user._id}>
                              {user?.profile?.name || user?.email || user?.username || 'Unknown User'}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Next Action Due Date
                        </label>
                        <input
                          type="date"
                          name="nextActionDueDate"
                          value={formData.nextActionDueDate}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                          onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Next Action
                        </label>
                        <input
                          type="text"
                          name="nextAction"
                          value={formData.nextAction}
                          onChange={handleInputChange}
                          placeholder="e.g., Call back, Send proposal"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                          onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '4px',
                          }}
                        >
                          Notes
                        </label>
                        <textarea
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          placeholder="Add notes about this lead"
                          rows="2"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            color: '#111827',
                            boxSizing: 'border-box',
                            resize: 'vertical',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                          onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Fields Section */}
              {customFields.length > 0 && (
                <div
                  style={{
                    marginBottom: '20px',
                    padding: '16px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      marginBottom: expandedSections.customFields ? '12px' : '0',
                    }}
                    onClick={() => toggleSection('customFields')}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151',
                      }}
                    >
                      Custom Fields
                    </h4>
                    <button
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#000000',
                        fontSize: '20px',
                        padding: '4px 8px',
                      }}
                    >
                      {expandedSections.customFields ? '−' : '+'}
                    </button>
                  </div>

                  {expandedSections.customFields && (
                    <div>
                      {customFields.map((field) => {
                        const value = getCustomFieldValue(field.fieldKey);
                        const fieldType = getCustomFieldType(field.fieldKey);
                        const options = getCustomFieldOptions(field.fieldKey);
                        const isRequired = isCustomFieldRequired(field.fieldKey);

                        return (
                          <div key={field.fieldKey} style={{ marginBottom: '12px' }}>
                            <label
                              style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#111827',
                                marginBottom: '4px',
                              }}
                            >
                              {getCustomFieldLabel(field.fieldKey)}
                              {isRequired && <span style={{ color: '#EF4444' }}>*</span>}
                            </label>

                            {fieldType === 'text' && (
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                placeholder={`Enter ${getCustomFieldLabel(field.fieldKey)}`}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  outline: 'none',
                                  fontSize: '14px',
                                  color: '#111827',
                                  boxSizing: 'border-box',
                                  transition: 'border-color 0.2s',
                                }}
                                onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                                onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                              />
                            )}

                            {fieldType === 'textarea' && (
                              <textarea
                                value={value}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                placeholder={`Enter ${getCustomFieldLabel(field.fieldKey)}`}
                                rows={3}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  outline: 'none',
                                  fontSize: '14px',
                                  color: '#111827',
                                  boxSizing: 'border-box',
                                  resize: 'vertical',
                                  transition: 'border-color 0.2s',
                                }}
                                onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                                onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                              />
                            )}

                            {fieldType === 'number' && (
                              <input
                                type="number"
                                value={value}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                placeholder={`Enter ${getCustomFieldLabel(field.fieldKey)}`}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  outline: 'none',
                                  fontSize: '14px',
                                  color: '#111827',
                                  boxSizing: 'border-box',
                                  transition: 'border-color 0.2s',
                                }}
                                onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                                onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                              />
                            )}

                            {fieldType === 'date' && (
                              <input
                                type="date"
                                value={value}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  outline: 'none',
                                  fontSize: '14px',
                                  color: '#111827',
                                  boxSizing: 'border-box',
                                  transition: 'border-color 0.2s',
                                }}
                                onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                                onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                              />
                            )}

                            {fieldType === 'dropdown' && options.length > 0 && (
                              <select
                                value={value}
                                onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  outline: 'none',
                                  fontSize: '14px',
                                  color: '#111827',
                                  cursor: 'pointer',
                                  appearance: 'none',
                                  boxSizing: 'border-box',
                                  backgroundImage:
                                    'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20stroke%3D%22%236B7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right 14px center',
                                  backgroundSize: '16px',
                                }}
                              >
                                <option value="">Select {getCustomFieldLabel(field.fieldKey)}</option>
                                {options.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            )}

                            {fieldType === 'checkbox' && (
                              <div>
                                {options.length > 0 ? (
                                  options.map((option) => (
                                    <label
                                      key={option}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '6px',
                                        fontSize: '14px',
                                        color: '#374151',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={value && value.includes(option)}
                                        onChange={(e) => {
                                          let newValue = value ? value.split(',') : [];
                                          if (e.target.checked) {
                                            newValue.push(option);
                                          } else {
                                            newValue = newValue.filter((item) => item !== option);
                                          }
                                          handleCustomFieldChange(field.fieldKey, newValue.join(','));
                                        }}
                                        style={{
                                          width: '16px',
                                          height: '16px',
                                          cursor: 'pointer',
                                          accentColor: '#7C3AED',
                                        }}
                                      />
                                      {option}
                                    </label>
                                  ))
                                ) : (
                                  <input
                                    type="text"
                                    value={value}
                                    onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                    placeholder="Enter checkbox options (comma separated)"
                                    style={{
                                      width: '100%',
                                      padding: '8px 12px',
                                      backgroundColor: '#fff',
                                      border: '1px solid #E5E7EB',
                                      borderRadius: '6px',
                                      outline: 'none',
                                      fontSize: '14px',
                                      color: '#111827',
                                      boxSizing: 'border-box',
                                      transition: 'border-color 0.2s',
                                    }}
                                    onFocus={(e) => (e.target.style.borderColor = '#7C3AED')}
                                    onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                                  />
                                )}
                              </div>
                            )}

                            {fieldType === 'radio' && options.length > 0 && (
                              <div>
                                {options.map((option) => (
                                  <label
                                    key={option}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      marginBottom: '6px',
                                      fontSize: '14px',
                                      color: '#374151',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name={`custom_${field.fieldKey}`}
                                      value={option}
                                      checked={value === option}
                                      onChange={(e) => handleCustomFieldChange(field.fieldKey, e.target.value)}
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        cursor: 'pointer',
                                        accentColor: '#7C3AED',
                                      }}
                                    />
                                    {option}
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #F3F4F6',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: '#F9FAFB',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || isLoading}
            style={{
              padding: '8px 20px',
              backgroundColor: isSaving || isLoading ? '#9CA3AF' : '#7C3AED',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              color: '#fff',
              cursor: isSaving || isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {isSaving || isLoading ? (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ animation: 'spin 1s linear infinite' }}
                >
                  <style>
                    {`@keyframes spin { to { transform: rotate(360deg); } }`}
                  </style>
                  <line x1="12" y1="2" x2="12" y2="6"></line>
                  <line x1="12" y1="18" x2="12" y2="22"></line>
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                  <line x1="2" y1="12" x2="6" y2="12"></line>
                  <line x1="18" y1="12" x2="22" y2="12"></line>
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                  <line x1="16.24" y1="4.93" x2="19.07" y2="7.76"></line>
                </svg>
                Saving...
              </>
            ) : (
              'Create Lead'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddLeadModal;